import { useState, useEffect, useCallback } from 'react';
import { Clock, Coffee, Play, Users, DollarSign, TrendingUp } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../stores/authStore';
import { useShiftStore } from '../stores/shiftStore';
import { shiftApi } from '../services/api';
import { getShiftTypeConfig } from '../config/shiftTypeConfig';
import TimeClockModal from './TimeClockModal';

interface TimeClockProps {
  liveStats: {
    tablesServed: number;
    guestsServed: number;
    salesTotalCents: number;
    tipsTotalCents: number;
  };
}

export default function TimeClock({ liveStats }: TimeClockProps) {
  const { employee } = useAuthStore();
  const {
    shiftType,
    isOnBreak,
    startBreak,
    endBreak,
    updateBreakState,
  } = useShiftStore();

  const [showModal, setShowModal] = useState(false);
  const [isBreakLoading, setIsBreakLoading] = useState(false);

  // Server-synced state
  const [clockInTime, setClockInTime] = useState<Date | null>(null);
  const [totalBreakMinutes, setTotalBreakMinutes] = useState(0);
  const [currentBreakStart, setCurrentBreakStart] = useState<Date | null>(null);

  // Display state (updated every second)
  const [displayDuration, setDisplayDuration] = useState('0:00');
  const [displayBreakDuration, setDisplayBreakDuration] = useState('0:00');

  const shiftConfig = getShiftTypeConfig(shiftType);

  // Format minutes to H:MM
  const formatDuration = useCallback((minutes: number): string => {
    if (minutes < 0 || isNaN(minutes)) return '0:00';
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  }, []);

  // Format seconds to M:SS for break timer
  const formatBreakSeconds = useCallback((seconds: number): string => {
    if (seconds < 0 || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Fetch shift status from server
  const syncFromServer = useCallback(async () => {
    if (!employee?.id) return;

    try {
      const response = await shiftApi.getShiftStatus(employee.id);
      if (response.success && response.data?.timeEntry) {
        const entry = response.data.timeEntry;

        // Update local state from server
        setClockInTime(new Date(entry.clockIn));
        setTotalBreakMinutes(entry.breakMinutes || 0);

        if (entry.breakStart) {
          setCurrentBreakStart(new Date(entry.breakStart));
          updateBreakState(new Date(entry.breakStart), entry.breakMinutes || 0);
        } else {
          setCurrentBreakStart(null);
          updateBreakState(null, entry.breakMinutes || 0);
        }
      }
    } catch (err) {
      console.error('Failed to sync shift status:', err);
    }
  }, [employee?.id, updateBreakState]);

  // Initial sync and periodic refresh
  useEffect(() => {
    syncFromServer();
    const interval = setInterval(syncFromServer, 30000); // Sync every 30 seconds
    return () => clearInterval(interval);
  }, [syncFromServer]);

  // Update display every second
  useEffect(() => {
    const updateDisplay = () => {
      const now = Date.now();

      // Calculate shift duration
      if (clockInTime) {
        const totalMinutes = (now - clockInTime.getTime()) / 60000;

        // Current break duration in minutes
        let currentBreakMins = 0;
        if (currentBreakStart) {
          currentBreakMins = (now - currentBreakStart.getTime()) / 60000;
        }

        // Working time = total - all breaks
        const workingMinutes = totalMinutes - totalBreakMinutes - currentBreakMins;
        setDisplayDuration(formatDuration(workingMinutes));
      }

      // Break duration (in seconds for live display)
      if (currentBreakStart) {
        const breakSeconds = (now - currentBreakStart.getTime()) / 1000;
        setDisplayBreakDuration(formatBreakSeconds(breakSeconds));
      }
    };

    updateDisplay();
    const interval = setInterval(updateDisplay, 1000);
    return () => clearInterval(interval);
  }, [clockInTime, totalBreakMinutes, currentBreakStart, formatDuration, formatBreakSeconds]);

  const handleBreakToggle = async () => {
    if (!employee?.id) return;
    setIsBreakLoading(true);

    try {
      if (isOnBreak) {
        const response = await shiftApi.endBreak(employee.id);
        if (response.success) {
          endBreak(response.data?.breakMinutes || totalBreakMinutes);
          setCurrentBreakStart(null);
          setTotalBreakMinutes(response.data?.breakMinutes || totalBreakMinutes);
        }
      } else {
        const response = await shiftApi.startBreak(employee.id);
        if (response.success) {
          startBreak();
          setCurrentBreakStart(new Date());
        }
      }
    } catch (error) {
      console.error('Break toggle failed:', error);
    } finally {
      setIsBreakLoading(false);
    }
  };

  const formatCurrency = (cents: number) => {
    if (cents >= 100000) {
      return `$${(cents / 100000).toFixed(1)}k`;
    }
    return `$${(cents / 100).toFixed(0)}`;
  };

  const ShiftIcon = shiftConfig.icon;

  return (
    <>
      {/* Time Clock Bar */}
      <div
        className={clsx(
          'text-white px-4 py-2 flex items-center justify-between text-sm cursor-pointer transition-colors',
          isOnBreak
            ? 'bg-slate-600 hover:bg-slate-700'
            : 'bg-primary-700 hover:bg-primary-800'
        )}
        onClick={() => setShowModal(true)}
      >
        <div className="flex items-center gap-4">
          {/* Shift Type Badge */}
          <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-2 py-0.5">
            <ShiftIcon className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">{shiftConfig.label}</span>
          </div>

          {/* Shift Duration */}
          <div className="flex items-center gap-1.5">
            {isOnBreak ? (
              <Coffee className="w-4 h-4 animate-pulse" />
            ) : (
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            )}
            <span className="font-medium tabular-nums">
              {isOnBreak ? `Break: ${displayBreakDuration}` : displayDuration}
            </span>
          </div>

          {/* Tables served — hidden on phones so the Break button stays visible */}
          <div className="hidden sm:flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            <span>{liveStats.tablesServed} tables</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Sales — hidden on phones (also shown in the modal) */}
          <div className="hidden sm:flex items-center gap-1">
            <DollarSign className="w-3.5 h-3.5" />
            <span>{formatCurrency(liveStats.salesTotalCents)}</span>
          </div>

          {/* Tips — hidden on phones (also shown in the modal) */}
          <div className="hidden sm:flex items-center gap-1 opacity-80">
            <TrendingUp className="w-3.5 h-3.5" />
            <span>+{formatCurrency(liveStats.tipsTotalCents)} tips</span>
          </div>

          {/* Break Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleBreakToggle();
            }}
            disabled={isBreakLoading}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all',
              isOnBreak
                ? 'bg-white text-slate-700 hover:bg-slate-100'
                : 'bg-white/20 hover:bg-white/30',
              isBreakLoading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isOnBreak ? (
              <>
                <Play className="w-3 h-3" />
                <span>Resume</span>
              </>
            ) : (
              <>
                <Coffee className="w-3 h-3" />
                <span>Break</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Time Clock Modal */}
      <TimeClockModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        liveStats={liveStats}
        clockInTime={clockInTime}
        displayDuration={displayDuration}
        displayBreakDuration={displayBreakDuration}
        totalBreakMinutes={totalBreakMinutes}
        onBreakToggle={handleBreakToggle}
        isBreakLoading={isBreakLoading}
      />
    </>
  );
}
